import { BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { useState } from 'react'
import { StyleSheet } from 'react-native'

import { FigmaSearchIcon } from '../icons/FigmaSystemIcons'
import { Box, useTheme } from '../theme/restyle'

export interface SearchInputProps {
  value: string
  onChangeValue: (value: string) => void
  placeholder: string
  accessibilityLabel: string
}

/**
 * A single-line filter field for long pick lists.
 *
 * Uses gorhom's `BottomSheetTextInput`, so the screen hosting it must set
 * `keyboardAvoidEnabled` on `FlowSheet` — gorhom's native avoidance is inert
 * under `enableDynamicSizing` (see `useKeyboardHeight`).
 */
export function SearchInput({
  value,
  onChangeValue,
  placeholder,
  accessibilityLabel,
}: SearchInputProps) {
  const theme = useTheme()
  const [isFocused, setIsFocused] = useState(false)

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      borderRadius="element"
      style={[
        styles.field,
        {
          backgroundColor: theme.background.modal,
          borderColor: isFocused
            ? theme.border.elements.selected.color
            : theme.border.elements.default.color,
          borderWidth: theme.border.elements.default.weight,
        },
      ]}
    >
      <FigmaSearchIcon size={16} color={theme.content.elements.secondary} />
      <BottomSheetTextInput
        value={value}
        onChangeText={onChangeValue}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        placeholderTextColor={theme.content.elements.secondary}
        accessibilityLabel={accessibilityLabel}
        autoCapitalize="characters"
        autoCorrect={false}
        spellCheck={false}
        returnKeyType="search"
        style={[
          styles.input,
          theme.textVariants.body,
          { color: theme.content.elements.primary },
        ]}
      />
    </Box>
  )
}

const styles = StyleSheet.create({
  field: { height: 48, paddingLeft: 16, paddingRight: 8, gap: 8 },
  // Undo the platform's own padding so the field's height is the box's.
  input: { flex: 1, padding: 0 },
})
